import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Button, Form, Row, Col, Table } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaFileExcel, FaRedo } from 'react-icons/fa';
import { toast } from 'react-toastify';
import transactionService from '../../services/transactionService';
import PaginationComponent from '../../components/PaginationComponent';
import ImportModal from '../import/ImportModal';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { usePagination } from '../../hooks/usePagination';
import { useAppContext } from '../../contexts/AppContext';
import CategorySelect from '../../components/CategorySelect';
import initialBalanceService from '../../services/initialBalanceService';

const Transactions = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { categories, creditCards } = useAppContext();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    description: '',
    categoryId: '',
    startDate: '',
    endDate: '',
    nature: ''
  });
  const [showImportModal, setShowImportModal] = useState(false);
  const [importText, setImportText] = useState('');
  const [importType, setImportType] = useState('SPREADSHEET');
  const [importSubtype, setImportSubtype] = useState('WITHOUT_CARD');
  const [selectedCreditCard, setSelectedCreditCard] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [initialBalance, setInitialBalance] = useState(0);

  // Paginação usando custom hook
  const {
    currentPage,
    itemsPerPage,
    totalPages,
    currentItems: currentTransactions,
    totalItems,
    handlePageChange,
    handleItemsPerPageChange,
    resetToFirstPage
  } = usePagination(transactions);

  // Buscar saldo inicial
  useEffect(() => {
    const loadInitialBalance = async () => {
      try {
        const data = await initialBalanceService.getCurrentBalance();
        setInitialBalance(data?.amount || 0);
      } catch (e) {
        setInitialBalance(0);
      }
    };
    loadInitialBalance();
  }, []);

  // Calcular saldo acumulado para cada transação
  const runningBalances = useMemo(() => {
    const balances = [];
    let balance = initialBalance;
    for (const transaction of transactions) {
      balance += transaction.amount;
      balances.push(balance);
    }
    return balances;
  }, [transactions, initialBalance]);



  const loadTransactions = useCallback(async () => {
    try {
      setLoading(true);
      
      const validFilters = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value) validFilters[key] = value;
      });
      
      const data = await transactionService.search(validFilters);
      setTransactions(data);
      
      // Resetar para a primeira página quando os filtros mudam
      resetToFirstPage();
    } catch (error) {
      console.error('Erro ao carregar transações:', error);
      toast.error('Erro ao carregar transações. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [filters, resetToFirstPage]);



  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  // Detectar parâmetro import=true na URL
  useEffect(() => {
    const shouldOpenImport = searchParams.get('import') === 'true';
    if (shouldOpenImport) {
      setShowImportModal(true);
      // Limpar o parâmetro da URL
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const handleEdit = useCallback((id) => {
    navigate(`/transactions/${id}`);
  }, [navigate]);

  const handleDelete = useCallback(async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta transação?')) {
      try {
        await transactionService.delete(id);
        toast.success('Transação excluída com sucesso!');
        loadTransactions();
      } catch (error) {
        console.error('Erro ao excluir transação:', error);
        toast.error('Erro ao excluir transação. Tente novamente.');
      }
    }
  }, [loadTransactions]);

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSearch = useCallback((e) => {
    e.preventDefault();
    loadTransactions();
  }, [loadTransactions]);

  const handleClearFilters = useCallback(() => {
    setFilters({
      description: '',
      categoryId: '',
      startDate: '',
      endDate: '',
      nature: ''
    });
  }, []);

  const handleImport = async () => {
    // Validações baseadas no novo formato hierárquico
    if (importSubtype !== 'WITHOUT_CARD' && !selectedCreditCard) {
      toast.error('Por favor, selecione um cartão de crédito.');
      return;
    }

    setImportLoading(true);
    try {
      let result;
      
      // Determinar qual endpoint chamar baseado na combinação tipo + subtipo
      if (importType === 'SPREADSHEET' && importSubtype === 'WITHOUT_CARD') {
        // Planilha > Sem cartão = importação de planilha atual
        result = await transactionService.importText(importText);
      } else if (importType === 'SPREADSHEET' && importSubtype !== 'WITHOUT_CARD') {
        // Planilha > Cartão X = importação de cartão atual
        result = await transactionService.importCreditCard(selectedCreditCard, importText);
      } else if (importType === 'BANK') {
        // TODO: Implementar importação de banco
        toast.info('Importação de banco será implementada em breve.');
        return;
      } else {
        toast.error('Combinação de tipo de importação inválida.');
        return;
      }
      
      if (result.ignored > 0) {
        toast.info(
          `Importado ${result.imported} de ${result.totalProcessed} registros com sucesso. ${result.ignored} registros foram ignorados (duplicados ou com dados inválidos).`
        );
      } else {
        toast.success(`Importado ${result.imported} de ${result.totalProcessed} registros com sucesso!`);
      }
      
      setShowImportModal(false);
      setImportText('');
      setImportType('SPREADSHEET');
      setImportSubtype('WITHOUT_CARD');
      setSelectedCreditCard('');
      loadTransactions();
    } catch (error) {
      console.error('Erro ao importar transações:', error);
      toast.error('Erro ao importar transações.');
    } finally {
      setImportLoading(false);
    }
  };

  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setImportText('');
    setImportType('SPREADSHEET');
    setImportSubtype('WITHOUT_CARD');
    setSelectedCreditCard('');
  };

  // Lógica para resetar subtipo quando mudar o tipo principal
  const handleImportTypeChange = (newType) => {
    setImportType(newType);
    setImportSubtype('WITHOUT_CARD');
    setSelectedCreditCard('');
  };

  // Lógica para gerenciar seleção de cartão quando mudar subtipo
  const handleImportSubtypeChange = (newSubtype) => {
    setImportSubtype(newSubtype);
    if (newSubtype === 'WITHOUT_CARD') {
      setSelectedCreditCard('');
    } else {
      setSelectedCreditCard(newSubtype); // newSubtype será o ID do cartão
    }
  };

  return (
    <Container data-testid="transactions-page">
      <div className="sheet-container">
        <div className="sheet-header">
          <h5 className="sheet-title">Transações</h5>
          <div className="sheet-toolbar">
            <Button 
              className="excel-btn"
              data-testid="new-transaction-button"
              onClick={() => navigate('/transactions/new')}
            >
              <FaPlus className="me-1" /> Nova Transação
            </Button>
            <Button 
              variant="outline-success"
              className="ms-2"
              onClick={() => setShowImportModal(true)}
            >
              <FaFileExcel className="me-1" /> Importar Transações
            </Button>
          </div>
        </div>
        
        <div className="p-3">
          <Form onSubmit={handleSearch}>
            <Row className="mb-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Descrição</Form.Label>
                  <Form.Control
                    type="text"
                    name="description"
                    value={filters.description}
                    onChange={handleFilterChange}
                    className="excel-form-control"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Categoria</Form.Label>
                  <CategorySelect
                    name="categoryId"
                    value={filters.categoryId}
                    onChange={handleFilterChange}
                    className="excel-form-control"
                    categories={categories}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Natureza</Form.Label>
                  <Form.Select
                    name="nature"
                    value={filters.nature}
                    onChange={handleFilterChange}
                    className="excel-form-control"
                  >
                    <option value="">Todas</option>
                    <option value="EXPENSE">Despesa</option>
                    <option value="INCOME">Receita</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Data Inicial</Form.Label>
                  <Form.Control
                    type="date"
                    name="startDate" data-testid="filter-start-date"
                    value={filters.startDate}
                    onChange={handleFilterChange}
                    className="excel-form-control"
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Data Final</Form.Label>
                  <Form.Control
                    type="date"
                    name="endDate" data-testid="filter-end-date"
                    value={filters.endDate}
                    onChange={handleFilterChange}
                    className="excel-form-control"
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex gap-2">
              <Button type="submit" className="excel-btn" data-testid="filter-button">
                <FaSearch className="me-1" /> Pesquisar
              </Button>
              <Button 
                type="button" 
                variant="outline-secondary"
                onClick={handleClearFilters}
              >
                Limpar Filtros
              </Button>
            </div>
          </Form>
        </div>
        
        <div className="p-3">
          {loading ? (
            <div className="text-center my-4">
              <span className="spinner-border text-primary" role="status"></span>
              <p className="mt-2">Carregando transações...</p>
            </div>
          ) : (
            <>
              <Table responsive hover data-testid="transactions-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Descrição</th>
                    <th>Categoria</th>
                    <th>Cartão</th>
                    <th>Natureza</th>
                    <th className="text-end">Valor</th>
                    <th className="text-end">Saldo</th>
                    <th className="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {currentTransactions.length > 0 ? (
                    currentTransactions.map((transaction, index) => {
                      const globalIndex = (currentPage - 1) * itemsPerPage + index;
                      const balance = runningBalances[globalIndex];
                      return (
                      <tr key={transaction.id} data-testid="transaction-row">
                        <td>{formatDate(transaction.date)}</td>
                        <td>
                          {transaction.description}
                          {transaction.recurrenceGroupId && (
                            <FaRedo className="ms-2 text-muted" title={`Recorrência ${transaction.recurrenceFrequency === 'MONTHLY' ? 'mensal' : transaction.recurrenceFrequency === 'WEEKLY' ? 'semanal' : 'anual'}`} style={{ fontSize: '0.75em' }} />
                          )}
                        </td>
                        <td>{transaction.category?.name || '-'}</td>
                        <td>{transaction.creditCard?.name || '-'}</td>
                        <td>{transaction.nature === 'EXPENSE' ? 'Despesa' : 'Receita'}</td>
                        <td className={`text-end ${transaction.nature === 'EXPENSE' ? 'text-danger' : 'text-success'}`}>
                          {formatCurrency(transaction.nature === 'EXPENSE' ? -transaction.amount : transaction.amount)}
                        </td>
                        <td className={`text-end ${balance < 0 ? 'text-danger' : ''}`}>
                          {formatCurrency(balance)}
                        </td>
                        <td className="text-center">
                          <div className="d-flex gap-2 justify-content-center">
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              onClick={() => handleEdit(transaction.id)}
                            >
                              <FaEdit />
                            </Button>
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={() => handleDelete(transaction.id)}
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center" data-testid="empty-message">Nenhuma transação encontrada</td>
                    </tr>
                  )}
                </tbody>
              </Table>
              
                             <PaginationComponent
                 currentPage={currentPage}
                 totalPages={totalPages}
                 itemsPerPage={itemsPerPage}
                 totalItems={totalItems}
                 onPageChange={handlePageChange}
                 onItemsPerPageChange={handleItemsPerPageChange}
                 itemName="transações"
               />
            </>
          )}
        </div>

        <ImportModal
          show={showImportModal}
          onHide={handleCloseImportModal}
          importText={importText}
          onImportTextChange={setImportText}
          importType={importType}
          onImportTypeChange={handleImportTypeChange}
          importSubtype={importSubtype}
          onImportSubtypeChange={handleImportSubtypeChange}
          creditCards={creditCards}
          onImport={handleImport}
          importLoading={importLoading}
        />
      </div>
    </Container>
  );
};

export default Transactions; 